import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Ship, Component, CompiledSystems } from '../../interfaces/types';
import { 
  FaFighterJet, FaSpaceShuttle, FaRocket, FaIndustry, 
  FaTools, FaTrophy, FaTruck, FaShip, FaPlane, FaTrash,
  FaPlaneDeparture, FaPencilAlt, FaDollarSign, FaBox, FaCar, FaUser, FaGamepad 
} from 'react-icons/fa';
import { 
  GiCargoShip, GiMilitaryFort, GiRaceCar, 
  GiSpaceship, GiBomber, GiMissilePod 
} from 'react-icons/gi';
import './ShipCard.css';
import { ImageCache } from '../../services/imageCache';
import { addShipToFleet, removeShipFromFleet } from '../../api/endpoints';
import { Tooltip } from 'react-tooltip';
import { showToast, showConfirmToast } from '../common/ToastContainer';
import { authService } from '../../services/authService';
import { PurchaseTypeModal } from '../common/PurchaseTypeModal';

interface ShipCardProps {
  ship: Ship;
  onClick: (ship: Ship) => void;
  duplicateCount: number;
  username?: string;
  isAnonymous: boolean;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showUsers?: boolean;
  isGrouped?: boolean;
  showCount?: boolean;
  onShipRemoved?: () => void;
  isCompareMode?: boolean;
  onCompareSelect?: (ship: Ship) => void;
  isSelectedForCompare?: boolean;
  selectedType?: string;
  selectedSize?: string;
  isAdmin?: boolean;
  isUserFiltered?: boolean;
  isFirstInList?: boolean;
}

const CATEGORY_NAMES: Record<string, string> = {
  RSIAvionic: 'Aviónica',
  RSIPropulsion: 'Propulsión',
  RSIThruster: 'Impulsores',
  RSIModular: 'Módulos',
  RSIWeapon: 'Armas'
};

const TYPE_ICONS: Record<string, JSX.Element> = {
  'combat': <FaFighterJet />,
  'transport': <FaTruck />,
  'exploration': <FaSpaceShuttle />,
  'industrial': <FaIndustry />,
  'support': <FaTools />,
  'competition': <FaTrophy />,
  'ground': <FaPlane />,
  'multi': <FaShip />,
  'cargo': <GiCargoShip />,
  'military': <GiMilitaryFort />,
  'starter': <FaRocket />,
  'racing': <GiRaceCar />,
  'expedition': <GiSpaceship />,
  'gunship': <GiMissilePod />,
  'bomber': <GiBomber />,
  'fighter': <FaFighterJet />
};

const getComponentTags = (compiled: CompiledSystems | null | undefined) => {
  if (!compiled) {
    return {};
  }

  const tagsByCategory: Record<string, { name: string; size: string }[]> = {};
  
  Object.entries(compiled).forEach(([category, systems]) => {
    const categoryName = CATEGORY_NAMES[category as keyof typeof CATEGORY_NAMES] || category;
    tagsByCategory[categoryName] = [];
    
    Object.entries(systems).forEach(([_, components]) => {
      (components as Component[]).forEach((component: Component) => {
        if (component.name) {
          tagsByCategory[categoryName].push({
            name: component.name,
            size: component.size || component.component_size || 'N/A'
          });
        }
      });
    });
  });

  return tagsByCategory;
};

// Añadir la constante para la imagen por defecto
const DEFAULT_SHIP_IMAGE = '/media/naves/not_found.png';

export const ShipCard: React.FC<ShipCardProps> = ({ 
  ship, 
  onClick, 
  duplicateCount, 
  username, 
  isAnonymous, 
  showAddButton = true, 
  showRemoveButton = false, 
  showUsers = false, 
  isGrouped, 
  showCount = true, 
  onShipRemoved, 
  isCompareMode = false, 
  onCompareSelect, 
  isSelectedForCompare = false,
  selectedType = '',
  selectedSize = '',
  isAdmin = false,
  isUserFiltered = false,
  isFirstInList = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shipImage, setShipImage] = useState<string>(ship.imagen);
  const [manufacturerImage, setManufacturerImage] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const loadImages = useCallback(async () => {
    try {
      // Cargar ambas imágenes en paralelo
      const [cachedShipImage, cachedManufacturerImage] = await Promise.all([
        // Si no hay imagen, usar la imagen por defecto
        ImageCache.getImage(ship.imagen || DEFAULT_SHIP_IMAGE),
        ImageCache.getImage(`/media/logos/${ship.manufacturer.code}.png`)
      ]);

      setShipImage(cachedShipImage || DEFAULT_SHIP_IMAGE);  // Usar imagen por defecto si falla la carga
      setManufacturerImage(cachedManufacturerImage);
    } catch (error) {
      setShipImage(DEFAULT_SHIP_IMAGE);  // Usar imagen por defecto en caso de error
    }
  }, [ship.imagen, ship.manufacturer.code]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isCompareMode) {
      setIsExpanded(prev => !prev);
    }
  }, [isCompareMode]);

  const componentTagsByCategory = useMemo(() => 
    getComponentTags(ship.compiled), 
    [ship.compiled]
  );

  const getAddTooltip = useCallback(() => {
    if (isAnonymous) {
      return "Añadir nave a la flota anónima";
    }
    if (username) {
      return `Añadir nave a la flota de ${username}`;
    }
    return "Debes identificarte o seleccionar modo anónimo para añadir naves";
  }, [username, isAnonymous]);

  const getSizeLetter = useCallback((size: string | null | undefined) => {
    if (!size) return '?';
    switch (size.toLowerCase()) {
      case 'snub': return 'N';
      case 'small': return 'S';
      case 'medium': return 'M';
      case 'large': return 'L';
      case 'capital': return 'C';
      default: return '?';
    }
  }, []);

  const getShipDimensionsTooltip = useCallback(() => {
    const dimensions = [];
    
    if (ship.length) {
      dimensions.push(`📏 Longitud: ${ship.length}m`);
    }
    if (ship.beam) {
      dimensions.push(`↔️ Anchura: ${ship.beam}m`);
    }
    if (ship.height) {
      dimensions.push(`📐 Altura: ${ship.height}m`);
    }
    if (ship.mass) {
      dimensions.push(`⚖️ Masa: ${ship.mass.toLocaleString()}kg`);
    }
    
    const sizeFormatted = ship.size 
      ? ship.size.charAt(0).toUpperCase() + ship.size.slice(1).toLowerCase()
      : 'Desconocido';
    
    if (dimensions.length === 0) {
      return `📦 Tamaño: ${sizeFormatted}`;
    }
    
    return `📦 ${sizeFormatted}\n\n${dimensions.join('\n')}`;
  }, [ship.length, ship.beam, ship.height, ship.mass, ship.size]);

  const handleAddToFleet = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!username && !isAnonymous) {
      showToast('Debes identificarte o seleccionar modo anónimo para añadir naves', 'warning');
      return;
    }

    const userIdentifier = isAnonymous ? 'anonymous' : username || '';
    if (!userIdentifier) {
      showToast("Error: No se ha especificado un usuario", 'error');
      return;
    }

    // Mostrar el modal para seleccionar tipo de adquisición
    setShowPurchaseModal(true);
  };

  const handlePurchaseTypeConfirm = async (isIngame: boolean, insurance?: string) => {
    setShowPurchaseModal(false);
    
    const userIdentifier = isAnonymous ? 'anonymous' : username || '';
    
    try {
      setIsAdding(true);
      await addShipToFleet({
        ship,
        username: userIdentifier,
        isAnonymous,
        ingame: isIngame,
        insurance: !isIngame ? insurance : undefined
      });
      
      let purchaseTypeText = isIngame ? 'in-game' : 'con dinero/créditos';
      if (!isIngame && insurance) {
        purchaseTypeText += ` - ${insurance}`;
      }
      
      showToast(`✅ La nave ${ship.name} (${purchaseTypeText}) ha sido añadida a tu flota`, 'success');
    } catch (error) {
      showToast(`❌ Error al añadir la nave: ${error instanceof Error ? error.message : 'Error desconocido'}`, 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveFromFleet = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!username && !isAnonymous) {
      showToast("Debes identificarte o seleccionar modo anónimo para eliminar naves", 'warning');
      return;
    }

    const userIdentifier = isAnonymous ? 'anonymous' : username || '';
    if (!userIdentifier) {
      showToast("Error: No se ha especificado un usuario", 'error');
      return;
    }

    const details = [
      `Fabricante: ${ship.manufacturer.name}`,
      `Tipo: ${ship.type || 'No especificado'}`,
      `Tamaño: ${ship.size || 'No especificado'}`
    ];

    showConfirmToast(
      `¿Estás seguro que quieres eliminar la nave ${ship.name} de tu flota?`,
      details,
      async () => {
        try {
          setIsRemoving(true);
          const result = await removeShipFromFleet(ship.name, ship.usuario || 'anonymous');
          showToast(`✅ ${result.message}`, 'success');
          onShipRemoved?.();
        } catch (error) {
          showToast(`❌ Error al eliminar la nave: ${error instanceof Error ? error.message : 'Error desconocido'}`, 'error');
        } finally {
          setIsRemoving(false);
        }
      }
    );
  };

  // Filtrar los usuarios según el rol
  const displayedUsers = React.useMemo(() => {
    if (!showUsers) return [];
    
    const userData = authService.getUserData();
    const isAdmin = userData?.admin === true;
    const isCapitan = userData?.capitan === true;
    
    if (isAdmin || isCapitan) {
      // Admin y Capitán pueden ver todas las etiquetas
      return isGrouped ? ship.usuarios || [] : [ship.usuario].filter(Boolean);
    } else {
      // Usuario normal solo ve su propia etiqueta
      if (isGrouped) {
        return (ship.usuarios || []).filter(user => user === username);
      } else {
        return ship.usuario === username ? [ship.usuario] : [];
      }
    }
  }, [showUsers, isGrouped, ship.usuarios, ship.usuario, username]);

  // Determinar si se puede eliminar la nave
  const canRemoveShip = React.useMemo(() => {
    if (!showRemoveButton) return false;

    const userData = authService.getUserData();
    const isAdmin = userData?.admin === true;
    
    // Admin puede eliminar cualquier nave
    if (isAdmin) return true;
    
    // Otros usuarios solo pueden eliminar sus propias naves
    return ship.usuario === username;
  }, [showRemoveButton, ship.usuario, username]);

  return (
    <div 
      className={`ship-card ${isExpanded ? 'expanded' : ''} ${
        isCompareMode ? 'compare-mode' : ''
      } ${isSelectedForCompare ? 'selected-for-compare' : ''}`}
      onClick={handleClick}
    >
      <div className="ship-card__image">
        <img 
          src={shipImage} 
          alt={ship.name}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            if (img.src !== DEFAULT_SHIP_IMAGE) {
              img.src = DEFAULT_SHIP_IMAGE;
            }
          }}
        />
        
        {ship.production_status === 'flight-ready' && (
          <div className="ship-card__status-icon flight-ready" title="Flight Ready">
            <FaPlaneDeparture />
          </div>
        )}
        {ship.production_status === 'in-concept' && (
          <div className="ship-card__status-icon concept" title="In Concept">
            <FaPencilAlt />
          </div>
        )}
        {ship.url && (
          <a 
            href={`https://robertsspaceindustries.com${ship.url}`}
            className="ship-card__store-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Ver en la tienda"
          >
            <FaDollarSign />
          </a>
        )}
        
        {/* Mostrar propietario como etiqueta debajo del círculo del precio para Admin */}
        {isAdmin && !isGrouped && ship.usuario && ship.url && (
          <span className="owner-tag-price">
            <FaUser /> {ship.usuario}
          </span>
        )}
        
        {showCount && (
          <div className="ship-card__count">
            {isGrouped && ship.usuarios && ship.usuarios.length > 0 ? (
              isUserFiltered ? (
                <span className="my-ships-count">{duplicateCount}</span>
              ) : (
                <>
                  <span className="my-ships-count">{ship.usuarios.filter(user => user === username).length}</span>
                  <span>/{duplicateCount}</span>
                </>
              )
            ) : (
              <span className={ship.usuario === username ? 'my-ship-count' : ''}>{duplicateCount}</span>
            )}
          </div>
        )}
        
        {isCompareMode && (
          <div 
            className={`ship-card__compare ${isSelectedForCompare ? 'selected' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              onCompareSelect?.(ship);
            }}
          >
            <input
              type="checkbox"
              checked={isSelectedForCompare}
              onChange={() => {}}
              id={`compare-${ship.id}`}
            />
            <label htmlFor={`compare-${ship.id}`}>Comparar</label>
          </div>
        )}

        <div className="ship-card__manufacturer">
          <img src={manufacturerImage} alt={ship.manufacturer.name} />
        </div>

        <div className="ship-card__size">
          <div 
            className={`size-icon ${ship.size?.toLowerCase() === 'capital' ? 'capital' : ''} ${selectedSize && ship.size?.toLowerCase() === selectedSize.toLowerCase() ? 'filtered' : ''}`}
            data-tooltip-id="size-tooltip"
            data-tooltip-content={getShipDimensionsTooltip()}
            data-tooltip-place="top"
          >
            {ship.size?.toLowerCase() === 'vehicle' ? (
              <FaCar className="size-icon-vehicle" />
            ) : (
              <span className="size-letter">{getSizeLetter(ship.size)}</span>
            )}
          </div>
        </div>

        <div className="ship-card__type">
          <div className={`type-icon ${selectedType && ship.type?.toLowerCase() === selectedType.toLowerCase() ? 'filtered' : ''}`}>
            {ship.type && (TYPE_ICONS[ship.type.toLowerCase()] || <FaShip />)}
          </div>
        </div>

        <div className="ship-card__crew">
          <div className="crew-icon">
            <span className="crew-text">{`${ship.min_crew}-${ship.max_crew}`}</span>
          </div>
        </div>

        <div className="ship-card__cargo">
          <div className="cargo-icon">
            <FaBox />
            <span className="cargo-text">{ship.cargo_capacity || 0}</span>
          </div>
        </div>

        {/* Etiqueta de tipo de compra en el lado derecho (solo en flota y naves individuales) */}
        {showUsers && !isGrouped && ((isAdmin) || (ship.usuario === username)) && (
          <div className="ship-card__purchase-type">
            <div 
              className={`purchase-tag-icon ${ship.ingame ? 'in-game' : 'real-money'}`}
              data-tooltip-id="purchase-tooltip"
              data-tooltip-content={
                ship.ingame 
                  ? "Nave adquirida In-Game (aUEC)" 
                  : ship.insurance 
                    ? `Nave comprada con Dinero/Store Credits - ${ship.insurance}`
                    : "Nave comprada con Dinero/Store Credits"
              }
              data-tooltip-place="left"
            >
              {ship.ingame ? (
                <FaGamepad />
              ) : ship.insurance ? (
                <span className={`insurance-text-main ${ship.insurance.toLowerCase() === 'lti' ? 'lti' : 'months'}`}>
                  {ship.insurance.toLowerCase() === 'lti' ? 'LTI' : ship.insurance.replace(/\s*meses?/i, '')}
                </span>
              ) : (
                <FaDollarSign />
              )}
            </div>
          </div>
        )}

        {showAddButton && (
          <div 
            className={`ship-card__add ${isAdding ? 'adding' : ''}`}
            onClick={handleAddToFleet}
            data-tooltip-id="add-tooltip"
            data-tooltip-content={getAddTooltip()}
            {...(isFirstInList && { 'data-tutorial-target': 'add-ship-button' })}
          >
            <span>{isAdding ? '...' : '+1'}</span>
          </div>
        )}
        {canRemoveShip && (
          <div 
            className={`ship-card__remove ${isRemoving ? 'removing' : ''}`}
            onClick={handleRemoveFromFleet}
            data-tooltip-id="remove-tooltip"
            data-tooltip-content={
              !username && !isAnonymous 
                ? "Debes identificarte o seleccionar modo anónimo para eliminar naves"
                : `Eliminar nave de la flota de ${username}`
            }
            {...(ship.name === 'Aurora CL' && document.body.classList.contains('tutorial-step-9') && { 'data-tutorial-target': 'delete-ship-button-aurora-cl' })}
          >
            <span>{isRemoving ? '...' : <FaTrash />}</span>
          </div>
        )}
        {ship.name === 'Aurora CL' && (() => {
          console.log('AURORA CL FOUND! canRemoveShip:', canRemoveShip, 'username:', username, 'isAnonymous:', isAnonymous);
          return null;
        })()}
      </div>
      <div className="ship-card__content">
        <h2>
          {ship.usuario === username && (
            <span className="star-icon" style={{ color: '#98971A' }}>★</span>
          )}
          {ship.name}
        </h2>
        <div className="ship-card__details" style={{ display: isExpanded ? 'block' : 'none' }}>
          {showUsers && displayedUsers.length > 0 && (
            <div className="ship-card__users">
              {displayedUsers.map((user, index) => (
                <span key={index} className="user-tag">
                  <FaUser /> {user}
                </span>
              ))}
            </div>
          )}
          {/* Mostrar lista de propietarios para Admin (solo naves agrupadas) */}
          {isAdmin && isGrouped && ship.usuarios && ship.usuarios.length > 0 && (
            <div className="ship-card__owners">
              <h4>Propietarios:</h4>
              <div className="owners-list">
                {ship.usuarios.map((user, index) => (
                  <span key={index} className="owner-tag">
                    <FaUser /> {user}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Mostrar información de tipo de compra para naves agrupadas (solo en flota) */}
          {showUsers && isGrouped && ship.ingame !== undefined && ((isAdmin) || (ship.usuarios && username && ship.usuarios.includes(username))) && (
            <div className="ship-card__purchase-info">
              <h4>Tipo de adquisición:</h4>
              <span className={`purchase-tag ${ship.ingame ? 'in-game' : 'real-money'}`}>
                {ship.ingame ? (
                  <>
                    <FaGamepad /> In-Game
                  </>
                ) : (
                  <>
                    <FaDollarSign /> Dinero/Créditos
                  </>
                )}
              </span>
            </div>
          )}
          <h4>Componentes:</h4>
          {Object.entries(componentTagsByCategory).map(([category, tags]) => (
            <div key={category} className="tag-category">
              <div className="category-label">{category}</div>
              <div className="category-tags">
                {tags.map((tag, index) => (
                  <div key={index} className="component-tag">
                    <span className="tag-name">{tag.name}</span>
                    <span className="tag-size">{tag.size}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Tooltip id="add-tooltip" />
      <Tooltip id="remove-tooltip" />
      <Tooltip id="purchase-tooltip" />
      <Tooltip 
        id="size-tooltip" 
        style={{ 
          whiteSpace: 'pre-line',
          backgroundColor: '#3c3836',
          color: '#ebdbb2',
          border: '1px solid #665c54',
          borderRadius: '8px',
          padding: '12px',
          fontSize: '0.85rem',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          maxWidth: '200px'
        }}
      />
      
      <PurchaseTypeModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onConfirm={handlePurchaseTypeConfirm}
        shipName={ship.name}
      />
    </div>
  );
} 